import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, AlertCircle } from 'lucide-react'
import { toast } from 'react-toastify'
import { useCurrentUser } from '@/features/auth/hooks/use-current-user'
import { useOrganization, useUpdateOrganization, type BillingAddress } from '../hooks/use-organization'
import { cn } from '@/shared/lib/utils'

// Common countries list (ISO 3166-1 alpha-2)
const COUNTRIES = [
  { code: 'FR', name: 'France' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'DE', name: 'Germany' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'PT', name: 'Portugal' },
]

export function OrganizationTab() {
  const { t } = useTranslation()
  const { user } = useCurrentUser()
  const { data: organization, isLoading } = useOrganization()

  const [name, setName] = useState('')
  const [street, setStreet] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const [vatNumber, setVatNumber] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  const updateOrganization = useUpdateOrganization(user?.organization_id || '')

  // Initialize form with organization data
  useEffect(() => {
    if (organization) {
      setName(organization.name || '')
      const billing = organization.billing_address || {}
      setStreet(billing.street || '')
      setPostalCode(billing.postal_code || '')
      setCity(billing.city || '')
      setCountry(billing.country || '')
      setVatNumber(billing.vat_number || '')
    }
  }, [organization])

  // Track changes
  useEffect(() => {
    if (!organization) return

    const billing = organization.billing_address || {}
    const nameChanged = name !== (organization.name || '')
    const streetChanged = street !== (billing.street || '')
    const postalCodeChanged = postalCode !== (billing.postal_code || '')
    const cityChanged = city !== (billing.city || '')
    const countryChanged = country !== (billing.country || '')
    const vatChanged = vatNumber !== (billing.vat_number || '')

    setHasChanges(nameChanged || streetChanged || postalCodeChanged || cityChanged || countryChanged || vatChanged)
  }, [name, street, postalCode, city, country, vatNumber, organization])

  const validateForm = (): boolean => {
    let isValid = true

    if (name.trim().length < 2) {
      setNameError(t('settings.organization.nameMinLength'))
      isValid = false
    } else {
      setNameError(null)
    }

    return isValid
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    const billingAddress: BillingAddress = {
      street: street.trim() || undefined,
      postal_code: postalCode.trim() || undefined,
      city: city.trim() || undefined,
      country: country || undefined,
      vat_number: vatNumber.trim() || undefined,
    }

    try {
      await updateOrganization.mutateAsync({
        name: name.trim(),
        billing_address: billingAddress,
      })

      setHasChanges(false)
      toast.success(t('settings.organization.updateSuccess'))
    } catch {
      toast.error(t('common.error'))
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center rounded-xl border border-border bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-foreground-muted" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-xl border border-border bg-background">
      {/* Header */}
      <div className="border-b border-border px-5 py-4">
        <h2 className="text-sm font-semibold text-foreground">
          {t('settings.organization.companyInfo')}
        </h2>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-5 p-5">
        {/* Organization Name */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground">
            {t('settings.organization.name')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('settings.organization.namePlaceholder')}
            className={cn(
              'w-full rounded-lg border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle focus:outline-none focus:ring-1',
              nameError
                ? 'border-destructive focus:border-destructive focus:ring-destructive'
                : 'border-border focus:border-accent focus:ring-accent'
            )}
          />
          {nameError && (
            <p className="flex items-center gap-1 text-xs text-destructive">
              <AlertCircle className="h-3 w-3" />
              {nameError}
            </p>
          )}
        </div>

        {/* Billing Address Section */}
        <div className="flex flex-col gap-4">
          <h3 className="text-sm font-medium text-foreground">
            {t('settings.organization.billingAddress')}
          </h3>

          {/* Street */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-foreground-muted">
              {t('settings.organization.street')}
            </label>
            <input
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder={t('settings.organization.streetPlaceholder')}
              className="w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          {/* Postal Code + City */}
          <div className="flex gap-4">
            <div className="flex w-1/3 flex-col gap-1.5">
              <label className="text-sm text-foreground-muted">
                {t('settings.organization.postalCode')}
              </label>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder={t('settings.organization.postalCodePlaceholder')}
                className="w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <label className="text-sm text-foreground-muted">
                {t('settings.organization.city')}
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t('settings.organization.cityPlaceholder')}
                className="w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          </div>

          {/* Country */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-foreground-muted">
              {t('settings.organization.country')}
            </label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value="">{t('settings.organization.selectCountry')}</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* VAT Number */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-foreground-muted">
              {t('settings.organization.vatNumber')}
              <span className="ml-1 text-foreground-subtle">
                ({t('settings.organization.optional')})
              </span>
            </label>
            <input
              type="text"
              value={vatNumber}
              onChange={(e) => setVatNumber(e.target.value)}
              placeholder={t('settings.organization.vatNumberPlaceholder')}
              className="w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
            <p className="text-xs text-foreground-subtle">
              {t('settings.organization.vatHint')}
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border px-5 py-4">
        <button
          type="submit"
          disabled={!hasChanges || updateOrganization.isPending}
          className="flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {updateOrganization.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('settings.organization.saveChanges')}
        </button>
      </div>
    </form>
  )
}
